import axios from "axios";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import {
  Container,
  Link,
  Box,
  Button,
  Breadcrumbs,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import BasicVehicleDetailsView from "../../../components/BasicVehicleDetailsView";
import CreateTrip from "../../../components/CreateTrip";
import { StepperComponent } from "../../../components/Stepper";
import { getValidToken } from "../../../utils/authUtils";

interface FetchVehicleDetailsProps {
  showToast: any;
  navigate: any;
  setIsLoading: any;
  setVehicleDetails: any;
  vehicleId: string;
}

interface TripDetails {
  id: string;
  tripType: "Single" | "Round";
  journeyDates: {
    startDate: string;
    returnDate: string;
  };
  fromLocation: string;
  toLocation: string;
  otherFromLocation: string;
  otherToLocation: string;
  journeyStartOdometerReading: string;
  journeyEndOdometerReading: string;
}

interface CreateTripProps {
  data: any;
  showToast: any;
  navigate: any;
  setIsLoading: any;
}

export const locations = [
  { value: "borivali(mumbai)", label: "Borivali (Mumbai)" },
  { value: "wadibandar(mumbai)", label: "Wadibandar (Mumbai)" },
  { value: "mapusa(goa)", label: "Mapusa (Goa)" },
  { value: "panjim(goa)", label: "Panjim (Goa)" },
  { value: "margao(goa)", label: "Margao (Goa)" },
  { value: "pune", label: "Pune" },
  { value: "nashik", label: "Nashik" },
  { value: "bangalore", label: "Bangalore" },
  { value: "delhi", label: "Delhi" },
  { value: "ahmedabad", label: "Ahmedabad" },
  { value: "other", label: "Other" },
];

export const fetchVehicleDetails = async ({
  showToast,
  navigate,
  setIsLoading,
  setVehicleDetails,
  vehicleId,
}: FetchVehicleDetailsProps) => {
  try {
    const token = await getValidToken();

    const url = `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      setVehicleDetails(response.data.data || null);
    }
  } catch (error) {
    showToast("Failed to fetch vehicles", "error");
    console.error("Error fetching vehicles:", error);
  } finally {
    setIsLoading(false);
  }
};

const createTrip = async ({
  data,
  showToast,
  navigate,
  setIsLoading,
}: CreateTripProps) => {
  try {
    setIsLoading(true);
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips`;
    const response = await axios.post(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response?.data?.isSuccess) {
      showToast("Basic details saved successfully", "success");
      return response.data?.data;
    } else {
      showToast("Failed to save basic details", "error");
      return null;
    }
  } catch (error) {
    showToast("Failed to save basic details", "error");
    console.error("Error saving basic details:", error);
    return null;
  } finally {
    setIsLoading(false);
  }
};

const updateTripDetails = async ({
  data,
  showToast,
  setIsLoading,
  tripId,
  setCurrentStep,
}: any) => {
  try {
    setIsLoading(true);
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}`;

    const response = await axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response?.data?.isSuccess) {
      showToast("Trip details updated successfully", "success");
      setCurrentStep(1);
      return response.data?.data;
    } else {
      showToast("Failed to update trip details", "error");
      return null;
    }
  } catch (error) {
    showToast("Failed to update trip details", "error");
    console.error("Error updating trip details:", error);
  } finally {
    setIsLoading(false);
  }
};

export const fetchTripDetails = async ({
  setTripDetails,
  tripId,
  isCopyMode,
  newTripId,
}: any) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/basic-details`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.isSuccess) {
      const apiData = response?.data?.data?.trip;

      // Find if the locations exist in our predefined list
      const fromLocationMatch = locations.find(
        (loc) => loc.label === apiData?.fromLocation
      );
      const toLocationMatch = locations.find(
        (loc) => loc.label === apiData?.toLocation
      );

      // Transform API data to match UI state structure
      let transformedData;
      if (isCopyMode && !newTripId) {
        transformedData = {
          tripType: apiData?.tripType,
          fromLocation: fromLocationMatch ? fromLocationMatch.value : "other",
          toLocation: toLocationMatch ? toLocationMatch.value : "other",
          otherFromLocation: fromLocationMatch ? "" : apiData?.fromLocation,
          otherToLocation: toLocationMatch ? "" : apiData?.toLocation,
        };
      } else {
        transformedData = {
          id: apiData?.id,
          tripType: apiData?.tripType,
          journeyDates: {
            startDate: apiData?.journeyStartDate
              ? new Date(apiData?.journeyStartDate).toISOString().split("T")[0]
              : "",
            returnDate: apiData?.journeyReturnDate
              ? new Date(apiData?.journeyReturnDate).toISOString().split("T")[0]
              : "",
          },
          fromLocation: fromLocationMatch ? fromLocationMatch.value : "other",
          toLocation: toLocationMatch ? toLocationMatch.value : "other",
          otherFromLocation: fromLocationMatch ? "" : apiData?.fromLocation,
          otherToLocation: toLocationMatch ? "" : apiData?.toLocation,
          journeyStartOdometerReading: apiData?.vehicleOdometerStart,
          journeyEndOdometerReading: apiData?.vehicleOdometerEnd,
        };
      }
      setTripDetails(transformedData);
    }
  } catch (error) {
    console.error("Error fetching trip details:", error);
  }
};

const AddBasicTripDetails = () => {
  const { vehicleId, tripId: tripIdParam = null } = useParams();
  const { user, loading: authLoading } = useFirebaseAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [tripId, setTripId] = useState<string | null>(tripIdParam);
  const [vehicleDetails, setVehicleDetails] = useState<any | null>(null);
  const [tripDetails, setTripDetails] = useState<TripDetails>({
    id: "",
    tripType: "Round",
    journeyDates: {
      startDate: "",
      returnDate: "",
    },
    fromLocation: "borivali(mumbai)",
    toLocation: "mapusa(goa)",
    otherFromLocation: "",
    otherToLocation: "",
    journeyStartOdometerReading: "",
    journeyEndOdometerReading: "",
  });
  // Add state to track data changes
  const [hasDataChanged, setHasDataChanged] = useState(false);

  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const isCopyMode = searchParams.get("copy") === "true";
  const newTripId = searchParams.get("newTripId");

  useEffect(() => {
    if (user && vehicleId && !authLoading) {
      fetchVehicleDetails({
        showToast,
        navigate,
        setIsLoading,
        setVehicleDetails,
        vehicleId,
      });
      if (isEditMode || tripId || isCopyMode) {
        fetchTripDetails({
          setTripDetails,
          tripId: isCopyMode && newTripId ? newTripId : tripId,
          isCopyMode,
          newTripId,
        });
      }
    }
  }, [user, vehicleId, authLoading]);

  // Add effect to track data changes
  useEffect(() => {
    setHasDataChanged(true);
  }, [tripDetails]);

  const handleTripCreation = async () => {
    // Validate dates based on trip type
    if (
      tripDetails?.tripType === "Single" &&
      !tripDetails?.journeyDates?.startDate
    ) {
      showToast("Please select start date for single journey", "error");
      return;
    }

    if (
      tripDetails?.tripType === "Round" &&
      (!tripDetails?.journeyDates?.startDate ||
        !tripDetails?.journeyDates?.returnDate)
    ) {
      showToast(
        "Please select both start and return dates for round trip",
        "error"
      );
      return;
    }

    if (
      tripDetails?.journeyStartOdometerReading &&
      tripDetails?.journeyEndOdometerReading &&
      tripDetails?.journeyStartOdometerReading >
        tripDetails?.journeyEndOdometerReading
    ) {
      showToast(
        "Start odometer reading cannot be greater than end odometer reading",
        "error"
      );
      return;
    }

    try {
      // Only proceed with API calls if there are changes or no trip exists
      let createdTrip: any;
      if (hasDataChanged) {
        const tripData = {
          id: tripDetails?.id,
          tripType: tripDetails?.tripType,
          journeyStartDate: tripDetails?.journeyDates?.startDate
            ? new Date(tripDetails?.journeyDates?.startDate).toISOString()
            : new Date().toISOString(),
          journeyReturnDate:
            tripDetails?.tripType === "Round" &&
            tripDetails?.journeyDates?.returnDate
              ? new Date(tripDetails?.journeyDates?.returnDate).toISOString()
              : null,
          fromLocation:
            tripDetails?.fromLocation === "other"
              ? tripDetails?.otherFromLocation
              : locations.find((loc) => loc.value === tripDetails?.fromLocation)
                  ?.label || "",
          toLocation:
            tripDetails?.toLocation === "other"
              ? tripDetails?.otherToLocation
              : locations.find((loc) => loc.value === tripDetails?.toLocation)
                  ?.label || "",
          vehicleId: vehicleId,
          vehicleOdometerStart: tripDetails?.journeyStartOdometerReading,
          vehicleOdometerEnd: tripDetails?.journeyEndOdometerReading,
        };

        if (isEditMode || newTripId || (tripId && !isCopyMode)) {
          // Update existing trip
          await updateTripDetails({
            tripId: newTripId ? newTripId : tripId,
            data: tripData,
            user,
            showToast,
            setIsLoading,
            setCurrentStep,
          });
          if (newTripId) {
            navigate(
              `/trip/income/${vehicleId}/${tripId}?copy=${isCopyMode}&newTripId=${newTripId}`
            );
          } else {
            navigate(`/trip/income/${vehicleId}/${tripId}?edit=${isEditMode}`);
          }
        } else {
          // Create new trip
          const { id, ...tripDataWithoutId } = tripData;
          createdTrip = await createTrip({
            data: tripDataWithoutId,
            showToast,
            navigate,
            setIsLoading,
          });

          if (!createdTrip) {
            showToast("Failed to create trip", "error");
            return;
          }
          setHasDataChanged(false);
          if (isCopyMode) {
            navigate(
              `/trip/income/${vehicleId}/${tripId}?copy=${true}&newTripId=${
                createdTrip?.id
              }`
            );
          } else {
            navigate(`/trip/income/${vehicleId}/${createdTrip?.id}`);
          }
        }
      } else {
        // If no changes, just move to next step
        navigate(`/trip/income/${vehicleId}/${createdTrip?.id}`);
      }
    } catch (error) {
      console.error("Error in trip creation flow:", error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const [currentStep, setCurrentStep] = useState<number>(0);
  const steps = ["Basic Details", "Income Details", "Expense Details"];
  const handleStepClick = async (stepIndex: number) => {
    if (isEditMode) {
      if (stepIndex === 1) {
        navigate(`/trip/income/${vehicleId}/${tripId}?edit=${isEditMode}`);
      } else if (stepIndex === 2) {
        navigate(`/trip/expense/${vehicleId}/${tripId}?edit=${isEditMode}`);
      }
    } else {
      return;
    }
  };

  if (isLoading || authLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Breadcrumbs sx={{ mb: 2 }}>
          {isEditMode ? (
            <Link
              color="inherit"
              onClick={() => navigate("/trips")}
              sx={{ cursor: "pointer" }}
            >
              Trips Management
            </Link>
          ) : (
            <Link
              color="inherit"
              onClick={() => navigate("/trip/select-vehicle")}
              sx={{ cursor: "pointer" }}
            >
              Select Vehicle
            </Link>
          )}
          <Typography color="text.primary">Trip Details</Typography>
        </Breadcrumbs>
      </Box>

      <StepperComponent
        currentStep={currentStep}
        steps={steps}
        handleStepClick={handleStepClick}
        isEditMode={isEditMode}
      />
      {/* Vehicle Details */}
      <BasicVehicleDetailsView vehicle={vehicleDetails} />

      {/* Basic Trip Details */}
      <Box>
        <CreateTrip
          vehicleDetails={vehicleDetails}
          tripDetails={tripDetails}
          setTripDetails={setTripDetails}
          locations={locations}
          isEditMode={isEditMode}
        />
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          sx={{ mt: 3, mb: 3 }}
        >
          <Button
            variant="contained"
            onClick={handleTripCreation}
            endIcon={<ArrowForwardIcon />}
            disabled={
              (tripDetails?.tripType === "Single" &&
                !tripDetails?.journeyDates?.startDate) ||
              (tripDetails?.tripType === "Round" &&
                (!tripDetails?.journeyDates?.startDate ||
                  !tripDetails?.journeyDates?.returnDate))
            }
          >
            Save & Continue
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AddBasicTripDetails;
